// The Guardrails screen's view model (SCR-155, SCR-594): the definition the IaC builds a Bedrock guardrail
// from (content filters, denied topics, words, PII and regex actions, grounding, messages), its
// limits, the apply-to choice, which edits need a redeploy, and the list of guardrails the tab
// offers to apply (the definition, the stored id, the account's own). Pure and DOM-free
// (tests/unit/guardrails/model.test.ts); fetching and rendering live in guardrails-tab.tsx.
import type { components } from "../../api/schema";
import { getPath, isObject, setPath, type Json, type JsonObject } from "../../settings/inherit";
import { FIELDS, type SettingsErrors } from "../../settings/validate";

export type Strength = "NONE" | "LOW" | "MEDIUM" | "HIGH";
export type PiiAction = "BLOCK" | "ANONYMIZE";

export const STRENGTHS: readonly Strength[] = ["NONE", "LOW", "MEDIUM", "HIGH"];
export const PII_ACTIONS: readonly PiiAction[] = ["BLOCK", "ANONYMIZE"];

/** The six content filters in the order Bedrock lists them. `prompt_attack` has an input strength only. */
export const CONTENT_FILTERS = [
  { key: "hate", label: "Hate" },
  { key: "insults", label: "Insults" },
  { key: "sexual", label: "Sexual" },
  { key: "violence", label: "Violence" },
  { key: "misconduct", label: "Misconduct" },
  { key: "prompt_attack", label: "Prompt attack" },
] as const;
export type FilterKey = (typeof CONTENT_FILTERS)[number]["key"];

/** Bedrock requires the prompt-attack filter's output strength to be NONE; the grid shows it fixed. */
export const PROMPT_ATTACK_OUTPUT: Strength = "NONE";

export const LIMITS = {
  topics: 30,
  topicName: 100,
  topicDefinition: 200,
  topicExamples: 5,
  topicExample: 100,
  words: 10000,
  word: 100,
  piiEntities: 50,
  regexes: 10,
  regexName: 100,
  regexPattern: 500,
  message: 500,
  groundingMax: 0.99,
} as const;

const TOPIC_NAME = /^[0-9a-zA-Z\-_ !?.]{1,100}$/;
const PII_TYPE = /^[A-Z][A-Z0-9_]{1,63}$/;

/** `all` or any of these; the schema's apply_to enum. */
export const APPLY_TO = [
  { key: "all", label: "Every tool" },
  { key: "claude_code", label: "Claude Code" },
  { key: "codex", label: "Codex" },
  { key: "other", label: "Other tools" },
  { key: "web", label: "Web" },
] as const;

export const GROUNDING_NOTE = "Grounding and relevance checks compare an answer with reference text the request supplies. Coding tools send none, so these checks have no effect for them.";
export const REDEPLOY_NOTE = "A change to the guardrail's source or definition needs a redeploy of the proxy stack: the infrastructure code builds the guardrail. Changes to the switch, the tools, trace and stream mode apply within a minute.";

export interface Topic {
  name: string;
  definition: string;
  examples: string[];
}
export interface PiiEntity {
  type: string;
  action: PiiAction;
}
export interface Regex {
  name: string;
  pattern: string;
  action: PiiAction;
}
export interface Definition {
  content_filters: Record<Exclude<FilterKey, "prompt_attack">, { input: Strength; output: Strength }> & { prompt_attack: { input: Strength } };
  denied_topics: Topic[];
  word_filters: { profanity: boolean; words: string[] };
  sensitive_information: { pii_entities: PiiEntity[]; regexes: Regex[] };
  contextual_grounding: { grounding: number | null; relevance: number | null };
  blocked_input_message: string;
  blocked_output_message: string;
}

export function defaultDefinition(): Definition {
  const filter = () => ({ input: "HIGH" as Strength, output: "HIGH" as Strength });
  return {
    content_filters: { hate: filter(), insults: filter(), sexual: filter(), violence: filter(), misconduct: filter(), prompt_attack: { input: "HIGH" } },
    denied_topics: [],
    word_filters: { profanity: false, words: [] },
    sensitive_information: { pii_entities: [], regexes: [] },
    contextual_grounding: { grounding: null, relevance: null },
    blocked_input_message: "Your request was blocked by your organization's content policy.",
    blocked_output_message: "The response was blocked by your organization's content policy.",
  };
}

function strength(value: Json | undefined, fallback: Strength): Strength {
  return STRENGTHS.includes(value as Strength) ? (value as Strength) : fallback;
}
function action(value: Json | undefined): PiiAction {
  return value === "ANONYMIZE" ? "ANONYMIZE" : "BLOCK";
}
function text(value: Json | undefined, fallback = ""): string {
  return typeof value === "string" ? value : fallback;
}
function list(value: Json | undefined): Json[] {
  return Array.isArray(value) ? value : [];
}
function threshold(value: Json | undefined): number | null {
  return typeof value === "number" ? value : null;
}

/** The definition in a settings document (or the defaults when it has none), every field present. */
export function definitionOf(document: Json | undefined): Definition {
  const raw = getPath(document, "guardrails.definition");
  const base = defaultDefinition();
  if (!isObject(raw)) return base;
  const filters = isObject(raw["content_filters"]) ? raw["content_filters"] : {};
  for (const { key } of CONTENT_FILTERS) {
    const entry = filters[key];
    const found = isObject(entry) ? entry : {};
    if (key === "prompt_attack") base.content_filters.prompt_attack = { input: strength(found["input"], "HIGH") };
    else base.content_filters[key] = { input: strength(found["input"], "HIGH"), output: strength(found["output"], "HIGH") };
  }
  base.denied_topics = list(raw["denied_topics"])
    .filter(isObject)
    .map((t) => ({ name: text(t["name"]), definition: text(t["definition"]), examples: list(t["examples"]).filter((e): e is string => typeof e === "string") }));
  const words = isObject(raw["word_filters"]) ? raw["word_filters"] : {};
  base.word_filters = { profanity: words["profanity"] === true, words: list(words["words"]).filter((w): w is string => typeof w === "string") };
  const sensitive = isObject(raw["sensitive_information"]) ? raw["sensitive_information"] : {};
  base.sensitive_information = {
    pii_entities: list(sensitive["pii_entities"]).filter(isObject).map((e) => ({ type: text(e["type"]), action: action(e["action"]) })),
    regexes: list(sensitive["regexes"]).filter(isObject).map((r) => ({ name: text(r["name"]), pattern: text(r["pattern"]), action: action(r["action"]) })),
  };
  const grounding = isObject(raw["contextual_grounding"]) ? raw["contextual_grounding"] : {};
  base.contextual_grounding = { grounding: threshold(grounding["grounding"]), relevance: threshold(grounding["relevance"]) };
  base.blocked_input_message = text(raw["blocked_input_message"], base.blocked_input_message);
  base.blocked_output_message = text(raw["blocked_output_message"], base.blocked_output_message);
  return base;
}

/** The definition as the document stores it (JSON-safe, no extra keys). */
export function definitionJson(definition: Definition): JsonObject {
  return JSON.parse(JSON.stringify(definition)) as JsonObject;
}

// -------------------------------------------------------------------------------- content filters

export function setFilterStrength(definition: Definition, key: FilterKey, side: "input" | "output", value: Strength): Definition {
  const next = structuredClone(definition);
  if (key === "prompt_attack") {
    if (side === "input") next.content_filters.prompt_attack = { input: value };
  } else {
    next.content_filters[key][side] = value;
  }
  return next;
}

/** True when every filter is NONE: the guardrail would filter nothing in that part. */
export function filtersOff(definition: Definition): boolean {
  const { prompt_attack, ...rest } = definition.content_filters;
  return prompt_attack.input === "NONE" && Object.values(rest).every((f) => f.input === "NONE" && f.output === "NONE");
}

// ------------------------------------------------------------------------------------ list edits

export function addTopic(definition: Definition): Definition {
  if (definition.denied_topics.length >= LIMITS.topics) return definition;
  return { ...definition, denied_topics: [...definition.denied_topics, { name: "", definition: "", examples: [] }] };
}
export function updateTopic(definition: Definition, index: number, patch: Partial<Topic>): Definition {
  return { ...definition, denied_topics: definition.denied_topics.map((t, i) => (i === index ? { ...t, ...patch } : t)) };
}
export function removeTopic(definition: Definition, index: number): Definition {
  return { ...definition, denied_topics: definition.denied_topics.filter((_, i) => i !== index) };
}

/** One entry per non-blank line, trimmed. */
export function parseLines(textValue: string): string[] {
  return textValue.split("\n").map((line) => line.trim()).filter((line) => line !== "");
}

export function addPii(definition: Definition, type: string, piiAction: PiiAction = "BLOCK"): Definition {
  const clean = type.trim().toUpperCase();
  const have = definition.sensitive_information.pii_entities;
  if (clean === "" || have.length >= LIMITS.piiEntities || have.some((e) => e.type === clean)) return definition;
  return { ...definition, sensitive_information: { ...definition.sensitive_information, pii_entities: [...have, { type: clean, action: piiAction }] } };
}
export function updatePii(definition: Definition, index: number, piiAction: PiiAction): Definition {
  const entities = definition.sensitive_information.pii_entities.map((e, i) => (i === index ? { ...e, action: piiAction } : e));
  return { ...definition, sensitive_information: { ...definition.sensitive_information, pii_entities: entities } };
}
export function removePii(definition: Definition, index: number): Definition {
  const entities = definition.sensitive_information.pii_entities.filter((_, i) => i !== index);
  return { ...definition, sensitive_information: { ...definition.sensitive_information, pii_entities: entities } };
}

export function addRegex(definition: Definition): Definition {
  const have = definition.sensitive_information.regexes;
  if (have.length >= LIMITS.regexes) return definition;
  return { ...definition, sensitive_information: { ...definition.sensitive_information, regexes: [...have, { name: "", pattern: "", action: "BLOCK" }] } };
}
export function updateRegex(definition: Definition, index: number, patch: Partial<Regex>): Definition {
  const regexes = definition.sensitive_information.regexes.map((r, i) => (i === index ? { ...r, ...patch } : r));
  return { ...definition, sensitive_information: { ...definition.sensitive_information, regexes } };
}
export function removeRegex(definition: Definition, index: number): Definition {
  const regexes = definition.sensitive_information.regexes.filter((_, i) => i !== index);
  return { ...definition, sensitive_information: { ...definition.sensitive_information, regexes } };
}

// --------------------------------------------------------------------------------------- apply_to

/** `all` excludes the named tools; clearing the last choice falls back to `all`. */
export function toggleApplyTo(current: readonly string[], key: string): string[] {
  if (key === "all") return ["all"];
  const named = current.filter((k) => k !== "all");
  const next = named.includes(key) ? named.filter((k) => k !== key) : [...named, key];
  return next.length === 0 ? ["all"] : next;
}

export function applyToOf(document: Json | undefined): string[] {
  const raw = getPath(document, "guardrails.apply_to");
  const keys = list(raw).filter((k): k is string => typeof k === "string");
  return keys.length === 0 ? ["all"] : keys;
}

// ---------------------------------------------------------------------------------- validation

function trimmedLength(value: string): number {
  return value.trim().length;
}

function validGrounding(value: number | null): boolean {
  return value === null || (value >= 0 && value <= LIMITS.groundingMax);
}

/** Inline errors for the definition, by dotted path under `guardrails.definition`. */
export function validateDefinition(definition: Definition): SettingsErrors {
  const errors: SettingsErrors = {};
  const base = "guardrails.definition";
  if (definition.denied_topics.length > LIMITS.topics) errors[`${base}.denied_topics`] = `At most ${LIMITS.topics} topics.`;
  definition.denied_topics.forEach((topic, i) => {
    const at = `${base}.denied_topics.${i}`;
    if (!TOPIC_NAME.test(topic.name)) errors[`${at}.name`] = "Give a name of letters, digits, spaces and - _ ! ? . (up to 100).";
    if (trimmedLength(topic.definition) === 0 || topic.definition.length > LIMITS.topicDefinition) errors[`${at}.definition`] = `Describe the topic in 1 to ${LIMITS.topicDefinition} characters.`;
    if (topic.examples.length > LIMITS.topicExamples) errors[`${at}.examples`] = `At most ${LIMITS.topicExamples} examples.`;
    else if (topic.examples.some((e) => e.length > LIMITS.topicExample)) errors[`${at}.examples`] = `Each example is at most ${LIMITS.topicExample} characters.`;
  });
  const words = definition.word_filters.words;
  if (words.length > LIMITS.words) errors[`${base}.word_filters.words`] = `At most ${LIMITS.words} words.`;
  else if (new Set(words).size !== words.length) errors[`${base}.word_filters.words`] = "Words must be different.";
  else if (words.some((w) => w.length > LIMITS.word)) errors[`${base}.word_filters.words`] = `Each word is at most ${LIMITS.word} characters.`;
  const { pii_entities, regexes } = definition.sensitive_information;
  if (pii_entities.length > LIMITS.piiEntities) errors[`${base}.sensitive_information.pii_entities`] = `At most ${LIMITS.piiEntities} entity types.`;
  else if (pii_entities.some((e) => !PII_TYPE.test(e.type))) errors[`${base}.sensitive_information.pii_entities`] = "An entity type is capital letters, digits and underscores, such as US_SOCIAL_SECURITY_NUMBER.";
  if (regexes.length > LIMITS.regexes) errors[`${base}.sensitive_information.regexes`] = `At most ${LIMITS.regexes} expressions.`;
  regexes.forEach((regex, i) => {
    const at = `${base}.sensitive_information.regexes.${i}`;
    if (trimmedLength(regex.name) === 0 || regex.name.length > LIMITS.regexName) errors[`${at}.name`] = `Give a name of 1 to ${LIMITS.regexName} characters.`;
    if (regex.pattern === "" || regex.pattern.length > LIMITS.regexPattern) errors[`${at}.pattern`] = `Give a pattern of 1 to ${LIMITS.regexPattern} characters.`;
    else if (!compiles(regex.pattern)) errors[`${at}.pattern`] = "This is not a valid regular expression.";
  });
  if (!validGrounding(definition.contextual_grounding.grounding)) errors[`${base}.contextual_grounding.grounding`] = `A threshold from 0 to ${LIMITS.groundingMax}, or empty to leave the check off.`;
  if (!validGrounding(definition.contextual_grounding.relevance)) errors[`${base}.contextual_grounding.relevance`] = `A threshold from 0 to ${LIMITS.groundingMax}, or empty to leave the check off.`;
  for (const key of ["blocked_input_message", "blocked_output_message"] as const) {
    const message = definition[key];
    if (trimmedLength(message) === 0 || message.length > LIMITS.message) errors[`${base}.${key}`] = `Give a message of 1 to ${LIMITS.message} characters.`;
  }
  return errors;
}

function compiles(pattern: string): boolean {
  try {
    new RegExp(pattern);
    return true;
  } catch {
    return false;
  }
}

/** Errors for the document's guardrails: the existing id and version, and the definition when it is the source. */
export function validateGuardrails(document: Json | undefined): SettingsErrors {
  const errors: SettingsErrors = {};
  const guardrails = getPath(document, "guardrails");
  if (!isObject(guardrails)) return errors;
  if (guardrails["mode"] === "existing") {
    const existing = guardrails["existing"];
    const found = isObject(existing) ? existing : {};
    if (typeof found["id"] !== "string" || found["id"] === "") errors["guardrails.existing.id"] = "Give the guardrail's id.";
    if (typeof found["version"] !== "string" || found["version"] === "") errors["guardrails.existing.version"] = "Give a version number or DRAFT.";
  } else if (guardrails["enabled"] === true && isObject(guardrails["definition"])) {
    Object.assign(errors, validateDefinition(definitionOf(document)));
  }
  return errors;
}

// ------------------------------------------------------------------------------------ redeploy

/** The guardrails fields the annotations say take effect only after a redeploy, by path. */
export function redeployPaths(fields = FIELDS): string[] {
  return fields.filter((f) => f.section === "guardrails" && f.path.split(".").length === 2 && f.apply === "redeploy").map((f) => f.path);
}

/** The redeploy-only guardrails paths that differ between the saved document and the draft. */
export function pendingRedeploy(saved: Json | undefined, draft: Json | undefined, paths = redeployPaths()): string[] {
  return paths.filter((path) => JSON.stringify(getPath(saved, path) ?? null) !== JSON.stringify(getPath(draft, path) ?? null));
}

const PATH_LABELS: Record<string, string> = {
  "guardrails.mode": "the guardrail source",
  "guardrails.existing": "the existing guardrail",
  "guardrails.definition": "the guardrail definition",
};

/** "Saving changes the guardrail definition; the proxy stack needs a redeploy." or null. */
export function redeployText(paths: readonly string[]): string | null {
  if (paths.length === 0) return null;
  const names = paths.map((p) => PATH_LABELS[p] ?? p);
  return `Saving changes ${names.join(" and ")}; the proxy stack needs a redeploy for it to take effect.`;
}

// ------------------------------------------------------------------------------------ the list

export type ListedGuardrail = components["schemas"]["InstallationGuardrail"];
export type GuardrailListing = components["schemas"]["InstallationGuardrailList"];

/** Where an applied guardrail comes from: the document's definition, or one the account has. */
export type GuardrailSource = { kind: "defined" } | { kind: "existing"; id: string; version: string };

/** One row of the Guardrails card. */
export interface GuardrailRow {
  key: string;
  title: string;
  detail: string;
  source: GuardrailSource;
  applied: boolean;
  /** False for a listed guardrail Bedrock reports FAILED or DELETING: applying it would fail every request closed. */
  applicable: boolean;
  /** A listed guardrail's version choice; null for the definition and a stored id. */
  versions: RowVersions | null;
}

/** One entry of a listed guardrail's version picker. */
export interface VersionOption {
  value: string;
  label: string;
}

/** A listed row's versions: what the picker offers, what it starts on, and a newer one than the applied. */
export interface RowVersions {
  options: VersionOption[];
  /** The applied version when this guardrail is applied, otherwise `defaultVersion`. */
  initial: string;
  /** The latest version when the applied one is an older numbered version; null otherwise. */
  newer: string | null;
}

const UNUSABLE_STATUSES = new Set(["FAILED", "DELETING"]);

const EXISTING_ID = /^[a-z0-9]{12}$/;
const EXISTING_VERSION = /^([1-9][0-9]{0,4}|DRAFT)$/;

function storedExisting(document: Json | undefined): { id: string; version: string } | null {
  const existing = getPath(document, "guardrails.existing");
  if (!isObject(existing) || typeof existing["id"] !== "string" || existing["id"] === "") return null;
  return { id: existing["id"], version: text(existing["version"]) };
}

/** The guardrail the document applies, or null when it applies none. */
export function appliedSource(document: Json | undefined): GuardrailSource | null {
  if (getPath(document, "guardrails.enabled") !== true) return null;
  if (getPath(document, "guardrails.mode") !== "existing") return { kind: "defined" };
  const existing = storedExisting(document);
  return existing ? { kind: "existing", ...existing } : null;
}

function sameSource(a: GuardrailSource | null, b: GuardrailSource): boolean {
  if (a === null || a.kind !== b.kind) return false;
  return a.kind === "defined" || (b.kind === "existing" && a.id === b.id && a.version === b.version);
}

function plural(count: number, one: string, many: string): string {
  return `${count} ${count === 1 ? one : many}`;
}

/** "1 denied topic, 2 PII entities, 0 expressions; filters all HIGH" (or each filter's strengths when they differ). */
export function definitionSummary(definition: Definition): string {
  const counts = [plural(definition.denied_topics.length, "denied topic", "denied topics"), plural(definition.sensitive_information.pii_entities.length, "PII entity", "PII entities"), plural(definition.sensitive_information.regexes.length, "expression", "expressions")];
  const strengths = CONTENT_FILTERS.map(({ key, label }) => {
    const filter = definition.content_filters[key];
    return { label, values: "output" in filter ? [filter.input, filter.output] : [filter.input] };
  });
  const all = strengths.flatMap((s) => s.values);
  const filters = all.every((s) => s === all[0]) ? `filters all ${all[0]}` : `filters ${strengths.map((s) => `${s.label} ${s.values.join("/")}`).join(", ")}`;
  return `${counts.join(", ")}; ${filters}`;
}

/** Bedrock edits a guardrail's DRAFT in place; a numbered version is a pinned snapshot of it. */
const DRAFT_NOTE = "the editable working copy; pick a numbered version with 'Use a guardrail by id'";
const DRAFT_OPTION = "DRAFT — editable working copy";
const VERSIONS_UNREAD = "versions could not be read";

/** "v3" for a numbered version, "DRAFT" for the working copy. */
export function versionLabel(version: string): string {
  return version === "DRAFT" ? "DRAFT" : `v${version}`;
}

/** The versions a listed guardrail offers: its numbered ones newest first, then DRAFT; only DRAFT when they could not be read. */
export function versionOptions(item: ListedGuardrail): VersionOption[] {
  const draft = { value: "DRAFT", label: DRAFT_OPTION };
  if (item.versionsUnavailable) return [draft];
  const numbered = item.versions.map(({ version }) => ({ value: version, label: version === item.latestVersion ? `${versionLabel(version)} — latest` : versionLabel(version) }));
  return [...numbered, draft];
}

/** The version a listed guardrail's picker starts on: its latest, or DRAFT when it has none (or they could not be read). */
export function defaultVersion(item: ListedGuardrail): string {
  return item.versionsUnavailable ? "DRAFT" : (item.latestVersion ?? "DRAFT");
}

/** The latest version when `applied` is an older numbered version of the guardrail; null otherwise. */
export function newerVersion(applied: string, item: ListedGuardrail): string | null {
  const latest = defaultVersion(item);
  if (applied === "DRAFT" || latest === "DRAFT") return null;
  return Number(applied) < Number(latest) ? latest : null;
}

function listedVersions(item: ListedGuardrail): string {
  if (item.versionsUnavailable) return `${item.id} version DRAFT — ${VERSIONS_UNREAD}`;
  if (item.versions.length === 0) return `${item.id} version DRAFT — ${DRAFT_NOTE}`;
  const numbered = item.versions.map(({ version }) => (version === item.latestVersion ? `${versionLabel(version)} (latest)` : versionLabel(version)));
  return [item.id, ...numbered, "DRAFT"].join(" · ");
}

function listedDetail(item: ListedGuardrail): string {
  return [listedVersions(item), item.status, item.description ?? ""].filter((part) => part !== "").join(" · ");
}

/** The version of `item` the document applies, when it applies one the picker offers; null otherwise. */
function appliedVersion(applied: GuardrailSource | null, item: ListedGuardrail, options: readonly VersionOption[]): string | null {
  if (applied?.kind !== "existing" || applied.id !== item.id) return null;
  return options.some((option) => option.value === applied.version) ? applied.version : null;
}

function rowVersions(applied: GuardrailSource | null, item: ListedGuardrail): RowVersions {
  const options = versionOptions(item);
  const current = appliedVersion(applied, item, options);
  return { options, initial: current ?? defaultVersion(item), newer: current === null ? null : newerVersion(current, item) };
}

function listsStored(listed: readonly ListedGuardrail[], stored: { id: string; version: string }): boolean {
  return listed.some((item) => item.id === stored.id && versionOptions(item).some((option) => option.value === stored.version));
}

/**
 * The rows the card lists: the document's definition when it has one, the stored existing id
 * when the account's list does not offer it, then each listed guardrail with its versions. Each
 * says whether the document applies it; a listed row's source is the version its picker starts on.
 */
export function guardrailRows(document: Json | undefined, listed: readonly ListedGuardrail[]): GuardrailRow[] {
  const applied = appliedSource(document);
  const rows: GuardrailRow[] = [];
  const row = (key: string, title: string, detail: string, source: GuardrailSource, applicable = true, versions: RowVersions | null = null) =>
    rows.push({ key, title, detail, source, applied: sameSource(applied, source), applicable, versions });
  if (isObject(getPath(document, "guardrails.definition"))) row("defined", "Defined in these settings", definitionSummary(definitionOf(document)), { kind: "defined" });
  const stored = storedExisting(document);
  if (stored && !listsStored(listed, stored)) {
    row(`stored:${stored.id}@${stored.version}`, `${stored.id}@${stored.version}`, "Named in these settings by id", { kind: "existing", ...stored });
  }
  for (const item of listed) {
    const versions = rowVersions(applied, item);
    row(`listed:${item.id}`, item.name, listedDetail(item), { kind: "existing", id: item.id, version: versions.initial }, !UNUSABLE_STATUSES.has(item.status), versions);
  }
  return rows;
}

const REASONS: Record<NonNullable<GuardrailListing["reason"]>, string> = {
  no_role: "Nexus has no verified account link for this installation whose role it can assume, so it cannot list guardrails (no_role).",
  access_denied: "The installer role in this account cannot list guardrails yet (access_denied).",
  region_unknown: "The installation names no region, so there is nowhere to list guardrails from (region_unknown).",
  upstream_error: "AWS did not answer the guardrail listing; try again later (upstream_error).",
};

/** Why the account's guardrails are missing from the card, in plain words; null when they are listed. */
export function listingNote(listing: GuardrailListing): string | null {
  if (listing.unavailable) return listing.reason ? REASONS[listing.reason] : "The guardrails in this account could not be listed.";
  return listing.items.length === 0 ? "This account has no guardrails in the installation's region yet." : null;
}

/** Errors for an id and version typed into "Use a guardrail by id", by field. */
export function existingErrors(id: string, version: string): { id?: string; version?: string } {
  const errors: { id?: string; version?: string } = {};
  if (!EXISTING_ID.test(id)) errors.id = "12 lower-case letters and digits, as Bedrock shows it.";
  if (!EXISTING_VERSION.test(version)) errors.version = "A version number or DRAFT.";
  return errors;
}

// ------------------------------------------------------------------------------- applying one

function withGuardrails(draft: JsonObject, values: Record<string, Json>): JsonObject {
  return Object.entries(values).reduce((next, [key, value]) => setPath(next, `guardrails.${key}`, value), draft);
}

/** The draft applying this source: on, its mode, and the existing id only when it names one. */
export function applySource(draft: JsonObject, source: GuardrailSource): JsonObject {
  if (source.kind === "defined") return withGuardrails(draft, { enabled: true, mode: "create", existing: null });
  return withGuardrails(draft, { enabled: true, mode: "existing", existing: { id: source.id, version: source.version } });
}

/** The draft with the guardrail off; the source stays for the next Apply. */
export function stopApplying(draft: JsonObject): JsonObject {
  return withGuardrails(draft, { enabled: false });
}

/**
 * "Create new guardrail": the draft with a definition started from the defaults as its source (a
 * document holds one definition, so this replaces any earlier one). The switch is left as it
 * is: when it is on, the new definition is what applies once the proxy is redeployed.
 */
export function createDefinition(draft: JsonObject): JsonObject {
  return withGuardrails(draft, { mode: "create", existing: null, definition: definitionJson(defaultDefinition()) });
}
